import Head from 'next/head';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Box, Page } from '@zillow/constellation-10';
import Layout from '../../components/Layout';
import PageHeading from '../../components/PageHeading';
import PropertyDropdown from '../../components/PropertyDropdown';
import ViewInMessagesButton from '../../components/ViewInMessagesButton';
import StringConstants from '../../constants/StringConstants';
import { MultiFamilyLeadsPageContextProvider } from '../../contexts/MultiFamilyLeadsContextProvider';
import getTimezoneData from '../../utils/dateTime/getTimezoneData';
import useMultiFamilyLeadsRouterHelper from '../../hooks/useMultiFamilyLeadsRouterHelper';
import TimeFrameDropdown, { DEFAULT_DAYS_BACK } from '../../components/TimeFrameDropdown';
import LeadSummaryCounts from '../../components/LeadSummaryCounts';
import LeadsTable from '../../components/LeadsTable';
import LeadsPageSkeleton from '../../components/LeadsPageSkeleton';
import { withRedirectBoundary } from '../../components/RedirectBoundary';
import {
  emitViewMfLeadsPageEvent,
  VIEW_MF_LEADS_PAGE_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/multiFamilyLeadsPage';
import { filterContainerStyles } from '../../styles/contactsPageStyles';

export default withRedirectBoundary(
  ({ user }) => {
    const { getIsDaysBackValid, getIsStartDateValid, updatePathWithTimeFrameDropdownValue } =
      useMultiFamilyLeadsRouterHelper();
    const router = useRouter();
    const { isEnterpriseCustomer } = user || {};

    const { query } = router;
    const { clientTimeZone } = getTimezoneData();
    const daysBackQueryParam = query.daysBack?.toString() || '';
    const daysBack = parseInt(daysBackQueryParam);
    const listingAlias = query.listingAlias?.toString();
    const startDate = query.startDate?.toString();
    const validDaysBackParam = daysBackQueryParam && getIsDaysBackValid(daysBack) ? daysBack : DEFAULT_DAYS_BACK;
    const validStartDateParam = startDate && getIsStartDateValid(startDate) ? startDate : '';
    const metaDataPageTitle = isEnterpriseCustomer
      ? StringConstants.MULTIFAMILY_LEADS_PAGE_TITLE_FOR_ENTERPRISE_USERS
      : StringConstants.MULTIFAMILY_LEADS_PAGE_TITLE;
    const pageTitle = isEnterpriseCustomer ? 'Contacts' : 'Leads';

    useEffect(() => {
      if (daysBack !== validDaysBackParam) {
        updatePathWithTimeFrameDropdownValue({
          timeFrameValue: validStartDateParam || validDaysBackParam,
          shouldReplaceHistory: true,
        });
      }
    }, []);

    useEffect(() => {
      emitViewMfLeadsPageEvent(user);
    }, []);

    return (
      <Layout>
        <Page.Content data-eventid={VIEW_MF_LEADS_PAGE_EVENT_TYPE_ID} data-eventdescription="Page view">
          <Head>
            <title>{metaDataPageTitle}</title>
          </Head>
          <MultiFamilyLeadsPageContextProvider
            value={{
              clientTimeZone,
              daysBack: validDaysBackParam.toString(),
              listingAlias,
              startDate: validStartDateParam,
            }}
          >
            <Box css={{ marginBottom: 'loose' }}>
              <PageHeading heading={pageTitle} />
            </Box>
            <Box css={filterContainerStyles}>
              <PropertyDropdown />
              <TimeFrameDropdown />
              <Box
                css={{
                  xxl: {
                    marginLeft: 'auto',
                  },
                }}
              >
                <ViewInMessagesButton />
              </Box>
            </Box>
            <LeadSummaryCounts />
            <LeadsTable />
          </MultiFamilyLeadsPageContextProvider>
        </Page.Content>
      </Layout>
    );
  },
  {
    loading: <LeadsPageSkeleton />,
  },
);
